import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { TenantProvider } from './context/TenantContext';
import PrivateRoute from './components/common/PrivateRoute';
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/common/Layout';
import Landing from './pages/Landing';
import Pricing from './pages/Pricing';
import Features from './pages/Features';
import Contact from './pages/Contact';
import IndustryTemplate from './pages/IndustryTemplate';

// Auth Pages
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import SubscriptionExpired from './components/auth/SubscriptionExpired';

// Subscription Pages
import SubscriptionPlans from './components/subscription/SubscriptionPlans';
import SubscriptionRenew from './components/subscription/SubscriptionRenew';
import SubscriptionDetails from './components/subscription/SubscriptionDetails';

// Main Pages
import Dashboard from './components/dashboard/Dashboard';
import POSInterface from './components/pos/POSInterface';
import ProductList from './components/inventory/ProductList';
import ProductForm from './components/inventory/ProductForm';
import CategoryList from './components/inventory/CategoryList';
import SupplierList from './components/inventory/SupplierList';
import BulkUpload from './components/inventory/BulkUpload';
import CategoryBulkUpload from './components/inventory/CategoryBulkUpload';
import LowStockAlert from './components/inventory/LowStockAlert';
import StockAdjust from './components/inventory/StockAdjust';
import StockMovements from './components/inventory/StockMovements';
import CustomerList from './components/customers/CustomerList';
import CustomerForm from './components/customers/CustomerForm';
import CustomerDetails from './components/customers/CustomerDetails';
import DebtList from './components/debts/DebtList';
import DebtDetails from './components/debts/DebtDetails';
import FinanceHub from './components/finance/FinanceHub';
import AiDashboard from './components/ai/AiDashboard';
import Reports from './components/reports/Reports';
import Settings from './components/settings/Settings';
import StaffList from './components/staff/StaffList';
import StaffForm from './components/staff/StaffForm';

// Admin Pages
import AdminDashboard from './components/admin/AdminDashboard';
import BusinessList from './components/admin/BusinessList';
import BusinessDetails from './components/admin/BusinessDetails';
import PlanList from './components/admin/PlanList';
import SystemUsers from './components/admin/SystemUsers';
import AuditLogs from './components/admin/AuditLogs';
import HomepageManager from './components/admin/HomepageManager';
import ModuleManager from './components/admin/ModuleManager';
import MpesaBilling from './components/admin/MpesaBilling';
import SubscriptionPayments from './components/admin/SubscriptionPayments';

function App() {
  return (
    <HelmetProvider>
      <Router>
      <AuthProvider>
        <TenantProvider>
          <CartProvider>
            <Toaster position="top-right" />
            <Routes>

              {/* ── Public Routes ── */}
              <Route path="/"                     element={<Landing />} />
              <Route path="/pricing"              element={<Pricing />} />
              <Route path="/features"             element={<Features />} />
              <Route path="/contact"              element={<Contact />} />
              <Route path="/hardware"             element={<IndustryTemplate />} />
              <Route path="/wholesale"            element={<IndustryTemplate />} />
              <Route path="/retail"               element={<IndustryTemplate />} />
              <Route path="/minimart"             element={<IndustryTemplate />} />
              <Route path="/electronics"          element={<IndustryTemplate />} />
              <Route path="/spare-parts"          element={<IndustryTemplate />} />
              <Route path="/agrovet"              element={<IndustryTemplate />} />
              <Route path="/login"                element={<Login />} />
              <Route path="/register"             element={<Register />} />
              <Route path="/forgot-password"      element={<ForgotPassword />} />
              <Route path="/reset-password"       element={<ResetPassword />} />
              <Route path="/subscription-expired" element={<SubscriptionExpired />} />
              <Route path="/settings/subscription/renew" element={
                <PrivateRoute checkSubscription={false}>
                  <SubscriptionRenew />
                </PrivateRoute>
              } />

              {/* ── Protected Routes ── */}
              {/* Layout wrapper — no path, just wraps all protected routes */}
              <Route element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/pos" element={
                  <ProtectedRoute requiredModule="pos">
                    <POSInterface />
                  </ProtectedRoute>
                } />

                {/* Inventory */}
                <Route path="/products" element={<ProtectedRoute requiredPermission="manage_products" requiredModule="inventory" />}>
                  <Route index              element={<ProductList />} />
                  <Route path="new"         element={<ProductForm />} />
                  <Route path="categories"  element={<CategoryList />} />
                  <Route path="categories/bulk-upload" element={<CategoryBulkUpload />} />
                  <Route path="suppliers"   element={<SupplierList />} />
                  <Route path="bulk-upload" element={<BulkUpload />} />
                  <Route path="low-stock"   element={<LowStockAlert />} />
                  <Route path="movements"   element={<StockMovements />} />
                  <Route path="stock/:id"   element={<StockAdjust />} />
                  <Route path=":id"         element={<ProductForm />} />
                </Route>

                {/* CRM */}
                <Route path="/customers" element={<ProtectedRoute requiredPermission="view_customers" requiredModule="crm" />}>
                  <Route index           element={<CustomerList />} />
                  <Route path="new"      element={<CustomerForm />} />
                  <Route path=":id"      element={<CustomerDetails />} />
                  <Route path=":id/edit" element={<CustomerForm />} />
                </Route>

                {/* Finance */}
                <Route path="/finance" element={
                  <ProtectedRoute requiredPermission="manage_debts" requiredModule="finance">
                    <FinanceHub />
                  </ProtectedRoute>
                } />
                <Route path="/debts" element={<ProtectedRoute requiredPermission="manage_debts" requiredModule="finance" />}>
                  <Route index      element={<DebtList />} />
                  <Route path=":id" element={<DebtDetails />} />
                </Route>

                {/* AI Business Intelligence */}
                <Route path="/ai" element={
                  <ProtectedRoute requiredPermission="view_reports" requiredModule="ai_bi">
                    <AiDashboard />
                  </ProtectedRoute>
                } />

                {/* Reports */}
                <Route path="/reports" element={
                  <ProtectedRoute requiredPermission="view_reports">
                    <Reports />
                  </ProtectedRoute>
                } />

                {/* Settings */}
                <Route path="/settings" element={<ProtectedRoute requiredPermission="manage_settings" />}>
                  <Route index                          element={<Settings />} />
                  <Route path="subscription"            element={<SubscriptionDetails />} />
                  <Route path="subscription/plans"      element={<SubscriptionPlans />} />
                  <Route path="profile"                 element={<Settings />} />
                  <Route path="users"                   element={<Settings />} />
                  <Route path="backup"                  element={<Settings />} />
                </Route>

                {/* Staff */}
                <Route path="/staff" element={<ProtectedRoute requiredPermission="manage_cashiers" />}>
                  <Route index      element={<StaffList />} />
                  <Route path="new" element={<StaffForm />} />
                  <Route path=":id" element={<StaffForm />} />
                </Route>

                {/* Admin */}
                <Route path="/admin">
                  <Route index             element={<AdminDashboard />} />
                  <Route path="businesses" element={<BusinessList />} />
                  <Route path="businesses/:id" element={<BusinessDetails />} />
                  <Route path="plans"      element={<PlanList />} />
                  <Route path="users"      element={<SystemUsers />} />
                  <Route path="homepage"   element={<HomepageManager />} />
                  <Route path="modules"    element={<ModuleManager />} />
                  <Route path="mpesa"      element={<MpesaBilling />} />
                  <Route path="subscription-payments" element={<SubscriptionPayments />} />
                  <Route path="audit"      element={<AuditLogs />} />
                </Route>

              </Route>

              {/* ── Catch-all ── */}
              <Route path="*" element={<Navigate to="/" replace />} />

            </Routes>
          </CartProvider>
        </TenantProvider>
      </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;